import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Icon,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    Text,
    useDisclosure,
    useToast,
} from '@chakra-ui/react';
import {
    ColumnDef,
    Row,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import FileSaver from 'file-saver';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { isArray, isEqual, omit } from 'lodash';

import PageHeader from 'layout/pageHeader';
import { useTranslation } from 'helpers/languages/translations';
import TablePagination from 'plugins/DITable/Pagination';
import { DITable } from 'plugins/DITable';
import GlobalFilter from 'plugins/DITable/filters/GlobalFilter';
import icons from 'shared/icons';
import { iconTableStyle, linkStyle, tableToolbarStyle } from 'shared/styles';
import { useLazyGetCompanyHistoryQuery } from 'core/API/historyApi';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { useExportNonAcceptedKeywordsMutation } from 'core/API/keywordsApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import dates from 'utils/dates';
import { entries, intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { EXCEL_FILE_TYPE } from 'shared/const';
import { IColor, IKeyword, TKeyword } from 'types';
import { TProductSplit } from 'shared/types';
import { HEADER_COMPANY, HEADER_USER } from 'pages/UserManagement/const';
import {
    IGNORED_COMPARED_FIELD,
    IHistory,
    TCategory,
    TFacUpdateChanges,
    THistoryEntry,
    TKeywordAddChanges,
    TKeywordDeleteChanges,
    TKeywordErrorChanges,
    TKeywordExportChanges,
    TKeywordUpdateChanges,
    TParamsType,
    TProductAddChanges,
    TProductDeleteChanges,
    TProductUpdateChanges,
    THistoryChanges,
    TProductKeywordsChanges,
    TProductKeywordsUploadParams,
    TKeywordCategoriesChanges,
    TKeywordCategoriesUploadParams,
    TListChanges,
    TColorsChanges,
    TDeleteChanges,
    IInternationalCompanyGroupsAddChanges,
    IInternationalCompanyGroupsUpdateChanges,
    TSalesDataUploadParams,
    TClusterHistoryChanges,
    TClusterChanges,
    TSsectorizationDataUploadChanges,
    TPDPUploadParams,
    TPDPUploadData,
    TPDPUploadRow,
    TPDPUploadModifiedRow,
} from './const';

const PATH = 'history';
const ID = 'cdm_historypage';
const NAME = 'page.history';
const MAX_KEYWORDS_TO_DISPLAY = 5;
const MAX_SEGMENTS_TO_DISPLAY = 5;
const MAX_CLUSTERS_TO_DISPLAY = 5;
const MAX_ICG_TO_DISPLAY = 5;
const MAX_MODIFIED_KEYWORDS_TO_DISPLAY = 1;

const HistoryViewer = () => {
    const [currentCategory, setCurrentCategory] = useState<keyof TParamsType | undefined>(undefined);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [modalBody, setModalBody] = useState<ReactNode>(null);
    const [getCompanyHistory, { data: historyEntries, isError, isLoading }] = useLazyGetCompanyHistoryQuery();
    const [exportNonAcceptedKeywords] = useExportNonAcceptedKeywordsMutation();
    const companyInfo = useGetCompanyInfoSelector();
    const translate = useTranslation();
    const toast = useToast();

    useEffect(() => {
        const getData = async () => {
            await getCompanyHistory();
        };

        getData().catch(console.error);
    }, []);

    useEffect(() => {
        setCurrentCategory(historyEntries?.histories[0] ? historyEntries?.histories[0].category : undefined);
    }, [isError, isLoading, historyEntries]);

    const data: Readonly<THistoryEntry[]> = useMemo(() => {
        if (!isLoading && !isError && historyEntries) {
            return historyEntries.histories.filter((h) => h.category === currentCategory);
        }
        return [];
    }, [isError, isLoading, historyEntries, currentCategory]);

    const handleDownloadNonAcceptedKeywords = async (nonAcceptedKeywords: TKeyword[]) => {
        const res = await exportNonAcceptedKeywords(nonAcceptedKeywords);
        if ('error' in res) throw new Error(translate('page.keyword.errorApiExport') as string);

        const blob = new Blob([res.data], {
            type: EXCEL_FILE_TYPE,
        });

        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        FileSaver.saveAs(
            blob,
            translate('page.keyword.exportWithErrorFileName', { name: companyInfo!.name_id }) as string,
        );
    };

    const filterByKeyword = (row: Row<any>, columnId: string, filterValue: any) => {
        const rowValue =
            typeof row.getValue(columnId) === 'object'
                ? JSON.stringify(row.getValue(columnId))
                : row.getValue(columnId);
        return String(rowValue).toLowerCase().includes(String(filterValue).toLowerCase());
    };

    const renderDifference = (title: string | null, val1: any, val2: any, isDetailed?: boolean, isAdd?: boolean) => {
        const ArrowIcon = icons.arrowGo;
        const size = isDetailed ? 100 : !title ? 40 : 30;

        return (
            <>
                <Flex w="100%" flexWrap="wrap" alignItems="center" mb="1">
                    {title && (
                        <Text mr="2" fontWeight="bold">
                            {title}:
                        </Text>
                    )}
                    {!isAdd && (
                        <>
                            <Box width={`${size}%`} bg="red.100">
                                <Badge w="100%" whiteSpace="normal" colorScheme="red">
                                    {isArray(val1) ? val1.join(', ') : val1}
                                </Badge>
                            </Box>
                            <Icon mx="2" as={ArrowIcon} style={isDetailed ? { margin: 'auto', rotate: '90deg' } : {}} />
                        </>
                    )}
                    <Box width={`${size}%`} bg="green.100">
                        <Badge w="100%" whiteSpace="normal" colorScheme="green">
                            {isArray(val2) ? val2.map((v) => v.id).join(', ') : val2}
                        </Badge>
                    </Box>
                </Flex>
                {isDetailed && <Divider borderWidth={1} my="3" />}
            </>
        );
    };

    const renderChangesDetailModal = () => {
        return (
            <Modal useInert={false} isCentered isOpen={isOpen} onClose={onClose} size={['sm', 'sm', '2xl']}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text as="h3">{`Changes`}</Text>
                    </ModalHeader>
                    <ModalCloseButton mt="2" />
                    <ModalBody fontSize="sm" maxH="80vh" overflowY="auto" pb="8" pr="8" pl="8">
                        {modalBody}
                    </ModalBody>
                </ModalContent>
            </Modal>
        );
    };

    const setAndShowModalData = (
        changes: THistoryChanges['changes'] = {},
        labelArray: Record<string, string>,
        isAdd?: boolean,
    ) => {
        setModalBody(
            entries(changes).map(([key, val]) => {
                const [val1, val2] = typeof val === 'string' ? ['', val] : val;
                return <>{renderDifference(labelArray[key] || key, val1, val2, true, isAdd)}</>;
            }),
        );
        onOpen();
    };

    const setAndShowModalList = (list: string[]) => {
        setModalBody(
            <Flex flexWrap="wrap" mt={3}>
                {list.map((url: string) => (
                    <Badge mr="1" mb="1" textTransform="none">
                        {url}
                    </Badge>
                ))}
            </Flex>,
        );
        onOpen();
    };

    const generateProductKeywordsModalData = (
        ean: string,
        keywordsChanges: TProductKeywordsChanges['keywordsChanges'],
    ) => {
        return (
            <Text as="h3" mb="2" fontWeight="bold">
                {translate('history.product_keywords.product.changes')}
                <Text as="span" fontWeight="normal" ml="2">
                    {ean}
                </Text>
                <Box mt="2">
                    {
                        <Flex flexDir="column">
                            {keywordsChanges.created?.length ? (
                                <Flex flexWrap="wrap">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.product_keywords.keywords.create')}
                                    </Text>
                                    {keywordsChanges.created.map((keyword) => (
                                        <Badge mr="1" mb="1">
                                            {keyword.keyword_name}
                                        </Badge>
                                    ))}
                                </Flex>
                            ) : null}
                            {keywordsChanges.deleted?.length ? (
                                <Flex flexWrap="wrap">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.product_keywords.keywords.delete')}
                                    </Text>
                                    {keywordsChanges.deleted.map((keyword) => (
                                        <Badge mr="1" mb="1">
                                            {keyword?.keyword_name}
                                        </Badge>
                                    ))}
                                </Flex>
                            ) : null}
                            {keywordsChanges.modified?.map((keyword) => (
                                <Flex flexDir="column">
                                    <Text mb="1">
                                        <Text as="span" fontWeight="bold" mr="2">
                                            {translate('history.product_keywords.keywords.change')}
                                        </Text>
                                        <Text as="span" fontWeight="normal">
                                            {keyword.keyword_name}
                                        </Text>
                                    </Text>
                                    {renderDifference(
                                        '',
                                        keyword.is_priority ? 'Non priority' : 'Priority',
                                        keyword.is_priority ? 'Priority' : 'Non priority',
                                    )}
                                </Flex>
                            ))}
                        </Flex>
                    }
                </Box>
            </Text>
        );
    };

    const generateKeywordCategoriesModalData = (categoriesChanges: TKeywordCategoriesChanges) => {
        return (
            <Text as="h3" mb="2" fontWeight="bold">
                <Box mt="2">
                    {
                        <Flex flexDir="column">
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {categoriesChanges.segmentsKeywords?.length
                                        ? translate('history.keyword_categories.assign_keywords')
                                        : translate('history.keyword_categories.delete_keywords')}
                                </Text>
                                {categoriesChanges.segmentsKeywords?.map((keyword) => (
                                    <Badge mr="1" mb="1">
                                        {keyword.name}
                                    </Badge>
                                ))}
                            </Flex>
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {categoriesChanges.segmentsKeywords?.length
                                        ? translate('history.keyword_categories.to_segments')
                                        : translate('history.keyword_categories.from_segments')}
                                </Text>
                                {categoriesChanges.updatedSegments?.map((seg) => (
                                    <Badge mr="1" mb="1">
                                        {seg.name}
                                    </Badge>
                                ))}
                            </Flex>
                        </Flex>
                    }
                </Box>
            </Text>
        );
    };

    const generateICGUpdateModalData = (changes: IInternationalCompanyGroupsUpdateChanges) => {
        return (
            <Text as="h3" fontWeight="bold">
                <Box>
                    {
                        <Flex flexDir="column">
                            {changes.name && (
                                <Text mb="3">
                                    {renderDifference(
                                        translate('history.icg.name') as string,
                                        changes.name.old,
                                        changes.name.new,
                                    )}
                                </Text>
                            )}
                            {changes.international_company && (
                                <Text mb="3">
                                    {renderDifference(
                                        translate('history.icg.international_cluster') as string,
                                        changes.international_company.old?.name,
                                        changes.international_company.new?.name,
                                    )}
                                </Text>
                            )}
                            {changes.company_groups?.added?.length && (
                                <Flex flexWrap="wrap" mb="3">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.added_company_groups')}:
                                    </Text>
                                    {changes.company_groups.added.map((cg) => (
                                        <Badge mr="1" mb="1">
                                            {cg.name}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                            {changes.company_groups?.deleted?.length && (
                                <Flex flexWrap="wrap" mb="3">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.deleted_company_groups')}:
                                    </Text>
                                    {changes.company_groups.deleted.map((cg) => (
                                        <Badge mr="1" mb="1">
                                            {cg.name}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                            {changes.users?.added?.length && (
                                <Flex flexWrap="wrap" mb="3">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.added_users')}:
                                    </Text>
                                    {changes.users.added.map((user) => (
                                        <Badge mr="1" mb="1">
                                            {user.username}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                            {changes.users?.deleted?.length && (
                                <Flex flexWrap="wrap" mb="3">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.deleted_users')}:
                                    </Text>
                                    {changes.users.deleted.map((user) => (
                                        <Badge mr="1" mb="1">
                                            {user.username}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                        </Flex>
                    }
                </Box>
            </Text>
        );
    };

    const renderKeywordCategoriesChanges = (params: TKeywordCategoriesChanges) => {
        const { updatedSegments, segmentsKeywords } = params;
        const updatedSegmentsLength = updatedSegments?.length || 0;
        const segmentsKeywordsLength = segmentsKeywords?.length || 0;
        const showMore =
            updatedSegmentsLength > MAX_SEGMENTS_TO_DISPLAY || segmentsKeywordsLength > MAX_KEYWORDS_TO_DISPLAY;
        const reducedSegments = updatedSegments?.slice(0, MAX_SEGMENTS_TO_DISPLAY);
        const reducedKeywords = segmentsKeywords?.slice(0, MAX_KEYWORDS_TO_DISPLAY);

        return (
            <Flex flexDir="column">
                <Text mb="1">
                    <Text as="span" mr="2">
                        {segmentsKeywords?.length
                            ? translate('history.keyword_categories.assign_keywords')
                            : translate('history.keyword_categories.delete_keywords')}
                    </Text>
                    {reducedKeywords.map((keyword) => (
                        <Badge mr="1" mb="1">
                            {keyword.name}
                        </Badge>
                    ))}
                    {segmentsKeywordsLength > MAX_KEYWORDS_TO_DISPLAY && '...'}
                </Text>
                <Text mb="1">
                    <Text as="span" mr="2">
                        {segmentsKeywords?.length
                            ? translate('history.keyword_categories.to_segments')
                            : translate('history.keyword_categories.from_segments')}
                    </Text>

                    {reducedSegments.map((seg) => (
                        <Badge mr="1" mb="1">
                            {seg.name}
                        </Badge>
                    ))}
                    {updatedSegmentsLength > MAX_SEGMENTS_TO_DISPLAY && '...'}
                </Text>
                {showMore && (
                    <Button
                        size="sm"
                        width={'fit-content'}
                        mt="2"
                        onClick={() => {
                            setModalBody(generateKeywordCategoriesModalData({ updatedSegments, segmentsKeywords }));
                            onOpen();
                        }}
                    >
                        {translate('global.show_more')}
                    </Button>
                )}
            </Flex>
        );
    };

    const renderUploadChanges = (params: unknown, category: TCategory) => {
        switch (category) {
            case 'PDP': {
                const uploadParams = (params as TPDPUploadParams)?.data as TPDPUploadData | undefined;

                const renderPDPUploadRow = (row: TPDPUploadRow) => (
                    <Flex flexDir="column" mb="3">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                EAN:
                            </Text>
                            {row.ean}
                        </Text>
                        {row.label ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Label:
                                </Text>
                                {row.label}
                            </Text>
                        ) : null}
                        {row.description ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Description:
                                </Text>
                                {row.description}
                            </Text>
                        ) : null}
                        {row.bulletPoints?.length ? (
                            <Flex flexWrap="wrap" mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Bullet points:
                                </Text>
                                {row.bulletPoints.map((bulletPoint) => (
                                    <Badge mr="1" mb="1">
                                        {bulletPoint}
                                    </Badge>
                                ))}
                            </Flex>
                        ) : null}
                        {row.nutriscore ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Nutriscore:
                                </Text>
                                {row.nutriscore}
                            </Text>
                        ) : null}
                        {row.week ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Week:
                                </Text>
                                {row.week}
                            </Text>
                        ) : null}
                    </Flex>
                );

                const renderPDPModifiedRow = (row: TPDPUploadModifiedRow) => (
                    <Flex flexDir="column" mb="3">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                EAN:
                            </Text>
                            {row.ean}
                        </Text>
                        {(Object.keys(row.newValues) as (keyof typeof row.newValues)[])
                            .filter((key) => key !== 'ean')
                            .map((key) =>
                                renderDifference(
                                    String(key),
                                    Array.isArray(row.oldValues[key])
                                        ? (row.oldValues[key] as string[]).join(', ')
                                        : row.oldValues[key],
                                    Array.isArray(row.newValues[key])
                                        ? (row.newValues[key] as string[]).join(', ')
                                        : row.newValues[key],
                                ),
                            )}
                    </Flex>
                );

                const hasChanges =
                    !!uploadParams &&
                    (uploadParams.added?.length || uploadParams.modified?.length || uploadParams.deleted?.length);

                return (
                    <Flex flexDir="column">
                        <Text mb="2">
                            <Text as="span" fontWeight="bold" mr="2">
                                File:
                            </Text>
                            {uploadParams?.fileName || '-'}
                        </Text>
                        {hasChanges ? (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                onClick={() => {
                                    setModalBody(
                                        <>
                                            {!uploadParams?.added?.length ? null : (
                                                <>
                                                    <Text as="h3" mb="2" fontWeight="bold">
                                                        Added PDPs
                                                    </Text>
                                                    {uploadParams.added.map((row) => renderPDPUploadRow(row))}
                                                </>
                                            )}

                                            {!uploadParams?.modified?.length ? null : (
                                                <>
                                                    <Divider borderWidth={1} my="3" />
                                                    <Text as="h3" mb="2" fontWeight="bold">
                                                        Modified PDPs
                                                    </Text>
                                                    {uploadParams.modified.map((row) => renderPDPModifiedRow(row))}
                                                </>
                                            )}

                                            {!uploadParams?.deleted?.length ? null : (
                                                <>
                                                    <Divider borderWidth={1} my="3" />
                                                    <Text as="h3" mb="2" fontWeight="bold">
                                                        Deleted PDPs
                                                    </Text>
                                                    {uploadParams.deleted.map((row) => renderPDPUploadRow(row))}
                                                </>
                                            )}
                                        </>,
                                    );
                                    onOpen();
                                }}
                            >
                                {translate('history.button.showChanges')}
                            </Button>
                        ) : null}
                    </Flex>
                );
            }
            case 'PRODUCT_KEYWORDS': {
                const uploadParams = (
                    params && (params as TProductKeywordsUploadParams).data
                        ? (params as TProductKeywordsUploadParams).data
                        : []
                ) as TProductKeywordsChanges[];

                return (
                    <Button
                        size="sm"
                        width={'fit-content'}
                        onClick={() => {
                            setModalBody(
                                <>
                                    {uploadParams
                                        .map((param) => {
                                            return generateProductKeywordsModalData(param.ean, param.keywordsChanges);
                                        })
                                        .reduce((acc, curr) => (
                                            <>
                                                {acc}
                                                <Divider borderWidth={1} my="3" />
                                                {curr}
                                            </>
                                        ))}
                                </>,
                            );
                            onOpen();
                        }}
                    >
                        {translate('history.button.showChanges')}
                    </Button>
                );
            }
            case 'KEYWORD_CATEGORIES': {
                const uploadParams = (
                    params && (params as TKeywordCategoriesUploadParams).data
                        ? (params as TKeywordCategoriesUploadParams).data
                        : []
                ) as TKeywordCategoriesChanges[];

                return (
                    <Button
                        size="sm"
                        width={'fit-content'}
                        onClick={() => {
                            setModalBody(
                                <>
                                    {uploadParams
                                        ?.map((param) => {
                                            return generateKeywordCategoriesModalData(param);
                                        })
                                        .reduce((acc, curr) => (
                                            <>
                                                {acc}
                                                <Divider borderWidth={1} my="3" />
                                                {curr}
                                            </>
                                        ))}
                                </>,
                            );
                            onOpen();
                        }}
                    >
                        {translate('history.button.showChanges')}
                    </Button>
                );
            }

            case 'SALES_DATA': {
                const uploadParams = (params as TSalesDataUploadParams)?.data;

                return (
                    <>
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.sales_data.file_name')}:
                            </Text>
                            {uploadParams?.fileName}
                        </Text>

                        {uploadParams?.comment && (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.sales_data.comment')}:
                                </Text>
                                {uploadParams?.comment}
                            </Text>
                        )}
                    </>
                );
            }

            case 'SECTORISATION': {
                const { shopsCount, retailers } = params as TSsectorizationDataUploadChanges;

                return (
                    <>
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.sectorisation.shops_count')}:
                            </Text>
                            {shopsCount}
                        </Text>
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.sectorisation.retailers')}:
                            </Text>
                            {retailers.join(', ')}
                        </Text>
                    </>
                );
            }

            default:
                return <></>;
        }
    };

    const createUpdateSection = (params: unknown, category: TCategory) => {
        switch (category) {
            case 'KEYWORD': {
                const { oldKeywords, newKeywords } = params as TKeywordUpdateChanges;
                const result: React.JSX.Element[] = [];

                oldKeywords.forEach((oldK, index) => {
                    const neededChanges: (keyof IKeyword)[] = [];
                    entries(oldK).forEach(([keywordProp, val]) => {
                        if (!IGNORED_COMPARED_FIELD.includes(keywordProp) && val !== newKeywords[index][keywordProp]) {
                            neededChanges.push(keywordProp);
                        }
                    });

                    const changesResults = neededChanges.map((prop) => {
                        return renderDifference(prop, oldK[prop], newKeywords[index][prop]);
                    });

                    if (oldK?.name)
                        result.push(
                            <Flex flexDir="column">
                                <Text mb="1">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.KEYWORD')}:
                                    </Text>
                                    {oldK.name}
                                </Text>
                                {changesResults}
                            </Flex>,
                        );
                });

                return result;
            }
            case 'USER': {
                const { nameId, changes = {} } = params as THistoryChanges;

                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.USER')}:
                            </Text>
                            {nameId}
                        </Text>
                        {entries(changes).map(([key, [val1, val2]]) =>
                            renderDifference((HEADER_USER as any)[key] || key, val1, val2),
                        )}
                    </Flex>
                );
            }
            case 'PICTURE': {
                const { nameId, changes = {} } = params as THistoryChanges;

                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.PICTURE')}:
                            </Text>
                            {nameId}
                        </Text>
                        {entries(changes).map(([key, [val1, val2]]) =>
                            renderDifference((HEADER_USER as any)[key] || key, val1, val2),
                        )}
                    </Flex>
                );
            }
            case 'CLUSTER': {
                const { changes = {} } = params as THistoryChanges;

                return (
                    <Flex flexDir="column">
                        {entries(changes).map(([key, [val1, val2]]) =>
                            renderDifference(translate(`cluster.${key}`) as string, val1, val2),
                        )}
                    </Flex>
                );
            }
            case 'SHOP_CLUSTER':
            case 'PRODUCT_CLUSTER': {
                const { shopId, ean, added = [], removed = [] } = params as TClusterHistoryChanges;
                const isShopCluster = category === 'SHOP_CLUSTER';

                return (
                    <Flex flexDir="column">
                        <Text mr="2" fontWeight="bold">
                            {isShopCluster ? translate('shopCluster.column.shop_id') : 'EAN'}:{' '}
                            {isShopCluster ? shopId : ean}
                        </Text>
                        {!added.length ? null : (
                            <Flex flexWrap="wrap">
                                <Text mr="2" fontWeight="bold">
                                    {translate('cluster.added')}:
                                </Text>
                                {added.map((label) => (
                                    <Badge mr="1" mb="1" bg="green.100">
                                        {label}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                        {!removed.length ? null : (
                            <Flex flexWrap="wrap">
                                <Text mr="2" fontWeight="bold">
                                    {translate('cluster.removed')}:
                                </Text>
                                {removed.map((label) => (
                                    <Badge mr="1" mb="1">
                                        {label}
                                    </Badge>
                                ))}
                            </Flex>
                        )}
                    </Flex>
                );
            }

            case 'KEYWORD_CATEGORIES': {
                return renderKeywordCategoriesChanges(params as TKeywordCategoriesChanges);
            }

            case 'COLORS': {
                const {
                    changes: {
                        color: [oldColor, newColor],
                    },
                    name,
                } = params as TColorsChanges;

                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.color.name')}:
                            </Text>
                            {name}
                        </Text>
                        {renderDifference(translate('history.color') as string, oldColor, newColor)}
                    </Flex>
                );
            }

            case 'PRODUCT_KEYWORDS': {
                const { ean, keywordsChanges } = params as TProductKeywordsChanges;
                const addedKeywordsLength = keywordsChanges.created?.length || 0;
                const deletedKeywordsLength = keywordsChanges.deleted?.length || 0;
                const modifiedKeywordsLength = keywordsChanges.modified?.length || 0;
                const showMore =
                    addedKeywordsLength > MAX_KEYWORDS_TO_DISPLAY ||
                    deletedKeywordsLength > MAX_KEYWORDS_TO_DISPLAY ||
                    modifiedKeywordsLength > MAX_MODIFIED_KEYWORDS_TO_DISPLAY;
                const reducedAddedKeywords = keywordsChanges.created?.slice(0, MAX_KEYWORDS_TO_DISPLAY);
                const reducedDeletedKeywords = keywordsChanges.deleted?.slice(0, MAX_KEYWORDS_TO_DISPLAY);
                const reducedModifiedKeywords = keywordsChanges.modified?.slice(0, MAX_MODIFIED_KEYWORDS_TO_DISPLAY);

                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {translate('history.product.ean')}
                            </Text>
                            {ean}
                        </Text>

                        {reducedAddedKeywords.length ? (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.product_keywords.keywords.create')}
                                </Text>
                                {reducedAddedKeywords.map((keyword) => (
                                    <Badge mr="1" mb="1">
                                        {keyword.keyword_name}
                                    </Badge>
                                ))}
                                {addedKeywordsLength > MAX_KEYWORDS_TO_DISPLAY && '...'}
                            </Flex>
                        ) : null}
                        {reducedDeletedKeywords?.length ? (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.product_keywords.keywords.delete')}
                                </Text>
                                {reducedDeletedKeywords?.map((keyword) => (
                                    <Badge mr="1" mb="1">
                                        {keyword.keyword_name}
                                    </Badge>
                                ))}
                                {deletedKeywordsLength > MAX_KEYWORDS_TO_DISPLAY && '...'}
                            </Flex>
                        ) : null}
                        {reducedModifiedKeywords?.length
                            ? reducedModifiedKeywords.map((keyword) => (
                                  <Flex flexDir="column">
                                      <Text mb="1">
                                          <Text as="span" fontWeight="bold" mr="2">
                                              {translate('history.product_keywords.keywords.change')}
                                          </Text>
                                          {keyword.keyword_name}
                                      </Text>
                                      {renderDifference(
                                          '',
                                          keyword.is_priority ? 'Non priority' : 'Priority',
                                          keyword.is_priority ? 'Priority' : 'Non priority',
                                      )}
                                  </Flex>
                              ))
                            : null}
                        {showMore && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                mt="2"
                                onClick={() => {
                                    setModalBody(generateProductKeywordsModalData(ean, keywordsChanges));
                                    onOpen();
                                }}
                            >
                                {translate('global.show_more')}
                            </Button>
                        )}
                    </Flex>
                );
            }
            case 'INTERNATIONAL_COMPANY_GROUPS': {
                const { name, international_company, users, company_groups } =
                    params as IInternationalCompanyGroupsUpdateChanges;
                const addedCompanyGroupsLength = company_groups?.added?.length || 0;
                const deletedCompanyGroupsLength = company_groups?.deleted?.length || 0;
                const addedUsersLength = users?.added?.length || 0;
                const deletedUsersLength = users?.deleted?.length || 0;
                const showMore =
                    addedCompanyGroupsLength > MAX_ICG_TO_DISPLAY ||
                    deletedCompanyGroupsLength > MAX_ICG_TO_DISPLAY ||
                    addedUsersLength > MAX_ICG_TO_DISPLAY ||
                    deletedUsersLength > MAX_ICG_TO_DISPLAY;
                const reducedAddedCompanyGroups = company_groups?.added?.slice(0, MAX_ICG_TO_DISPLAY);
                const reducedDeletedCompanyGroups = company_groups?.deleted?.slice(0, MAX_ICG_TO_DISPLAY);
                const reducedAddedUsers = users?.added?.slice(0, MAX_ICG_TO_DISPLAY);
                const reducedDeletedUsers = users?.deleted?.slice(0, MAX_ICG_TO_DISPLAY);

                return (
                    <Flex flexDir="column">
                        {name && (
                            <Text mb="1">
                                {renderDifference(translate('history.icg.name') as string, name?.old, name?.new)}
                            </Text>
                        )}

                        {international_company && (
                            <Text mb="1">
                                {renderDifference(
                                    translate('history.icg.international_cluster') as string,
                                    international_company.old?.name,
                                    international_company.new?.name,
                                )}
                            </Text>
                        )}

                        {reducedAddedCompanyGroups?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.added_company_groups')}:
                                </Text>
                                {reducedAddedCompanyGroups.map((cg) => (
                                    <Badge mr="1" mb="1">
                                        {cg.name}
                                    </Badge>
                                ))}
                                {addedCompanyGroupsLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}

                        {reducedDeletedCompanyGroups?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.deleted_company_groups')}:
                                </Text>
                                {reducedDeletedCompanyGroups.map((cg) => (
                                    <Badge mr="1" mb="1">
                                        {cg.name}
                                    </Badge>
                                ))}
                                {deletedCompanyGroupsLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}

                        {reducedDeletedUsers?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.deleted_users')}:
                                </Text>
                                {reducedDeletedUsers.map((user) => (
                                    <Badge mr="1" mb="1">
                                        {user.username}
                                    </Badge>
                                ))}
                                {deletedUsersLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}

                        {reducedAddedUsers?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.added_users')}:
                                </Text>
                                {reducedAddedUsers.map((user) => (
                                    <Badge mr="1" mb="1">
                                        {user.username}
                                    </Badge>
                                ))}
                                {addedUsersLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}

                        {showMore && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                mt="2"
                                onClick={() => {
                                    setModalBody(
                                        generateICGUpdateModalData(params as IInternationalCompanyGroupsUpdateChanges),
                                    );
                                    onOpen();
                                }}
                            >
                                {translate('global.show_more')}
                            </Button>
                        )}
                    </Flex>
                );
            }

            case 'LIST': {
                const { nameId, changes = {} } = params as THistoryChanges;
                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                {/* {translate('history.LIST')}: */}
                                List:
                            </Text>
                            {nameId}
                        </Text>
                        {changes.listDiff?.length
                            ? renderDifference('Difference', changes.listDiff[0], changes.listDiff[1])
                            : null}
                        {changes.nameId?.length ? renderDifference('Name', changes.nameId[0], changes.nameId[1]) : null}
                        {changes.status?.length
                            ? renderDifference('Status', changes.status[0], changes.status[1])
                            : null}
                        {changes.cellDiff ? (
                            <>
                                <Flex w="100%" flexWrap="wrap" alignItems="center" mb="1">
                                    <Text mr="2" fontWeight="bold">
                                        Cell changes:
                                    </Text>
                                    <Box bg="green.100">
                                        <Badge w="100%" whiteSpace="normal" colorScheme="green">
                                            {changes.cellDiff}
                                        </Badge>
                                    </Box>
                                </Flex>
                            </>
                        ) : null}
                    </Flex>
                );
            }
            case 'SECTORISATION': {
                const { nameId, changes = {} } = params as THistoryChanges;
                return (
                    <Flex flexDir="column">
                        <>
                            {Object.keys(changes).map((key) => {
                                if (changes[key]) {
                                    return renderDifference(key, changes[key][0], changes[key][1]);
                                }
                            })}
                        </>
                    </Flex>
                );
            }
            case 'COMPANY': {
                const { nameId, changes = {} } = params as THistoryChanges;
                const primaryChanges = ['name', 'is_custom_cg_sync_with_tools', 'tools'];
                const displayMoreBtn = !!entries(omit(changes, primaryChanges)).length;

                return (
                    <Flex justifyContent="space-between">
                        <Flex flexDir="column" flex={1}>
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.COMPANY')}:
                                </Text>
                                {nameId}
                            </Text>
                            {primaryChanges.map(
                                (key) =>
                                    changes[key] &&
                                    renderDifference((HEADER_COMPANY as any)[key], changes[key][0], changes[key][1]),
                            )}
                        </Flex>
                        {displayMoreBtn && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                onClick={() => {
                                    setAndShowModalData(changes, HEADER_COMPANY);
                                }}
                            >
                                {translate('global.show_more')}
                            </Button>
                        )}
                    </Flex>
                );
            }
            default: {
                const result: React.JSX.Element[] = [];
                if ('oldFac' in (params as any)) {
                    const { oldFac, newFac } = params as TFacUpdateChanges;
                    result.push(
                        renderDifference(
                            null,
                            oldFac.map((elem) => <Text>{translate('product.name_split_i', { idx: elem })}</Text>),
                            newFac.map((elem) => <Text>{translate('product.name_split_i', { idx: elem })}</Text>),
                        ),
                    );
                } else {
                    const { oldProducts, newProducts } = params as TProductUpdateChanges;

                    oldProducts?.forEach((oldP, index) => {
                        const changesRes: React.JSX.Element[] = [];
                        entries(oldP).forEach(([productProp, val]) => {
                            if (productProp !== 'ean') {
                                if (productProp.includes('name_split')) {
                                    const typedVal = (val || {}) as unknown as TProductSplit;
                                    const typedNewVal = (newProducts[index][productProp] ||
                                        {}) as unknown as TProductSplit;

                                    if ((typedVal.val || typedNewVal.val) && typedVal.val !== typedNewVal.val)
                                        changesRes.push(
                                            renderDifference(`${productProp}_value`, typedVal.val, typedNewVal.val),
                                        );
                                    if (!isEqual(typedVal.plus, typedNewVal.plus))
                                        changesRes.push(
                                            renderDifference(
                                                `${productProp}_plus`,
                                                typedVal.plus && typedVal.plus.length > 0
                                                    ? typedVal.plus.toString()
                                                    : '',
                                                typedNewVal.plus && typedNewVal.plus.length > 0
                                                    ? typedNewVal.plus.toString()
                                                    : '',
                                            ),
                                        );
                                    if (!isEqual(typedVal.minus, typedNewVal.minus))
                                        changesRes.push(
                                            renderDifference(
                                                `${productProp}_minus`,
                                                typedVal.minus && typedVal.minus.length > 0
                                                    ? typedVal.minus.toString()
                                                    : '',
                                                typedNewVal.minus && typedNewVal.minus.length > 0
                                                    ? typedNewVal.minus.toString()
                                                    : '',
                                            ),
                                        );
                                } else {
                                    changesRes.push(
                                        renderDifference(productProp, val, newProducts[index][productProp]),
                                    );
                                }
                            }
                        });

                        result.push(
                            <Flex flexDir="column">
                                <Text mb="1">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.product')}
                                    </Text>
                                    {oldP.ean}
                                </Text>
                                {changesRes}
                            </Flex>,
                        );
                    });
                }

                return result;
            }
        }
    };

    const generateICGUAddModalData = (changes: IInternationalCompanyGroupsAddChanges) => {
        return (
            <Text as="h3">
                <Box>
                    {
                        <Flex flexDir="column">
                            {changes.name && (
                                <Text fontWeight="normal">
                                    <Text as="span" fontWeight="bold">
                                        {translate('history.icg.name')}:
                                    </Text>{' '}
                                    {changes.name}
                                </Text>
                            )}

                            {changes.international_company && (
                                <Text fontWeight="normal">
                                    <Text as="span" fontWeight="bold">
                                        {translate('history.icg.international_cluster')}:
                                    </Text>{' '}
                                    {changes.international_company.name}
                                </Text>
                            )}
                            {changes.company_groups?.length && (
                                <Flex flexWrap="wrap">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.user_groups')}:
                                    </Text>
                                    {changes.company_groups.map((cg) => (
                                        <Badge mr="1" mb="1">
                                            {cg.name}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                            {changes.users?.length && (
                                <Flex flexWrap="wrap">
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.icg.users')}:
                                    </Text>
                                    {changes.users.map((user) => (
                                        <Badge mr="1" mb="1">
                                            {user.username}
                                        </Badge>
                                    ))}
                                </Flex>
                            )}
                        </Flex>
                    }
                </Box>
            </Text>
        );
    };

    const renderAddChanges = (params: any, category: TCategory) => {
        const displayBtn = (HEADERS: Record<string, string>) => (
            <Flex justifyContent="space-between">
                <Text>
                    <Text as="span" fontWeight="bold">
                        {translate(`history.${category}` as any)}
                    </Text>
                    : {(params as THistoryChanges).nameId}
                </Text>
                <Button
                    size="sm"
                    width={'fit-content'}
                    onClick={() => {
                        setAndShowModalData((params as THistoryChanges).changes, HEADERS, true);
                    }}
                >
                    {translate('global.show_more')}
                </Button>
            </Flex>
        );

        switch (category) {
            case 'KEYWORD':
                return <Text>{(params as TKeywordAddChanges).keyword}</Text>;
            case 'PRODUCT_INFO':
            case 'PRODUCT_TITLE':
                return <Text>{(params as TProductAddChanges).ean}</Text>;
            case 'PDP': {
                const { ean, label, description, bulletPoints, nutriscore, week } = params as TProductAddChanges & {
                    label?: string;
                    description?: string;
                    bulletPoints?: string[];
                    nutriscore?: string;
                    week?: string;
                };

                return (
                    <Flex flexDir="column">
                        <Text mb="1">
                            <Text as="span" fontWeight="bold" mr="2">
                                EAN:
                            </Text>
                            {ean}
                        </Text>
                        {label ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Label:
                                </Text>
                                {label}
                            </Text>
                        ) : null}
                        {description ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Description:
                                </Text>
                                {description}
                            </Text>
                        ) : null}
                        {bulletPoints?.length ? (
                            <Flex flexWrap="wrap" mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Bullet points:
                                </Text>
                                {bulletPoints.map((bulletPoint) => (
                                    <Badge mr="1" mb="1">
                                        {bulletPoint}
                                    </Badge>
                                ))}
                            </Flex>
                        ) : null}
                        {nutriscore ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Nutriscore:
                                </Text>
                                {nutriscore}
                            </Text>
                        ) : null}
                        {week ? (
                            <Text mb="1">
                                <Text as="span" fontWeight="bold" mr="2">
                                    Week:
                                </Text>
                                {week}
                            </Text>
                        ) : null}
                    </Flex>
                );
            }
            case 'USER':
                return displayBtn(HEADER_USER);
            case 'COMPANY':
                return displayBtn(HEADER_COMPANY);
            case 'PICTURE':
                return (
                    <>
                        <Flex>
                            <Text mr="2" fontWeight="bold">
                                {translate('pictures.upload.overwrite')}:
                            </Text>
                            {translate(params.meta?.overwrite ? 'yes' : 'no')}
                        </Flex>
                        <Flex>
                            <Text mr="2" fontWeight="bold" textTransform="capitalize">
                                {translate('pictures.format')}:
                            </Text>
                            {params.meta?.image_caractersitics}
                        </Flex>
                        <Flex>
                            <Text mr="2" fontWeight="bold" textTransform="capitalize">
                                {translate('pictures.type')}:
                            </Text>
                            {params.meta?.image_type}
                        </Flex>
                        <Flex flexWrap="wrap" mt={3}>
                            {params.list?.slice(0, 3).map((url: string) => (
                                <Badge mr="1" mb="1" textTransform="none">
                                    {url}
                                </Badge>
                            ))}
                        </Flex>
                        {params.list?.length > 3 && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                onClick={() => {
                                    setAndShowModalList(params.list);
                                }}
                            >
                                {translate('more')}
                            </Button>
                        )}
                    </>
                );
            case 'LIST':
                return (
                    <>
                        <Flex flexWrap="wrap">
                            <Text as="span" fontWeight="bold" mr="2">
                                List:
                            </Text>
                            {(params as THistoryChanges).nameId}
                        </Flex>
                        <Flex flexWrap="wrap">
                            <Text as="span" fontWeight="bold" mr="2">
                                Type:
                            </Text>
                            {(params as TListChanges).type}
                        </Flex>
                        <Flex flexWrap="wrap">
                            <Text as="span" fontWeight="bold" mr="2">
                                Status:
                            </Text>
                            {(params as TListChanges).active ? 'active' : 'inactive'}
                        </Flex>
                    </>
                );
            case 'COLORS': {
                const { name, color } = params as IColor;
                return (
                    <>
                        <Text mb="1">
                            <Text as="span" mr="2">
                                {translate('history.colors.set')}
                            </Text>

                            <Badge mr="1" mb="1">
                                {color}
                            </Badge>
                        </Text>
                        <Text mb="1">
                            <Text as="span" mr="2">
                                {translate('history.colors.for')}
                            </Text>

                            <Badge mr="1" mb="1">
                                {name}
                            </Badge>
                        </Text>
                    </>
                );
            }
            case 'INTERNATIONAL_COMPANY_GROUPS': {
                const { international_company, name, users, company_groups } =
                    params as IInternationalCompanyGroupsAddChanges;
                const addedCompanyGroupsLength = company_groups?.length || 0;
                const addedUsersLength = users?.length || 0;
                const showMore = addedCompanyGroupsLength > MAX_ICG_TO_DISPLAY || addedUsersLength > MAX_ICG_TO_DISPLAY;
                const reducedAddedCompanyGroups = company_groups?.slice(0, MAX_ICG_TO_DISPLAY);
                const reducedAddedUsers = users?.slice(0, MAX_ICG_TO_DISPLAY);

                return (
                    <>
                        <Flex>
                            <Text mr="2" fontWeight="bold">
                                {translate('history.icg.name')}:
                            </Text>
                            {name}
                        </Flex>

                        <Flex>
                            <Text mr="2" fontWeight="bold">
                                {translate('history.icg.international_cluster')}:
                            </Text>
                            {international_company?.name}
                        </Flex>

                        {reducedAddedCompanyGroups?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.user_groups')}:
                                </Text>
                                {reducedAddedCompanyGroups.map((cg) => (
                                    <Badge mr="1" mb="1">
                                        {cg.name}
                                    </Badge>
                                ))}
                                {addedCompanyGroupsLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}

                        {reducedAddedUsers?.length && (
                            <Flex flexWrap="wrap">
                                <Text as="span" fontWeight="bold" mr="2">
                                    {translate('history.icg.users')}:
                                </Text>
                                {reducedAddedUsers.map((user) => (
                                    <Badge mr="1" mb="1">
                                        {user.username}
                                    </Badge>
                                ))}
                                {addedUsersLength > MAX_ICG_TO_DISPLAY && '...'}
                            </Flex>
                        )}
                        {showMore && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                mt="2"
                                onClick={() => {
                                    setModalBody(
                                        generateICGUAddModalData(params as IInternationalCompanyGroupsAddChanges),
                                    );
                                    onOpen();
                                }}
                            >
                                {translate('global.show_more')}
                            </Button>
                        )}
                    </>
                );
            }
            case 'CLUSTER':
                const { label, labels } = params as TClusterChanges;

                if (params.labels) {
                    return (
                        <Flex>
                            <Box flexGrow={1}>
                                {labels?.slice(0, MAX_CLUSTERS_TO_DISPLAY).map((label) => (
                                    <Badge mr="1" mb="1" bg="green.100">
                                        {label}
                                    </Badge>
                                ))}
                            </Box>
                            {(labels?.length || 0) > MAX_CLUSTERS_TO_DISPLAY && (
                                <Button
                                    size="sm"
                                    width={'fit-content'}
                                    onClick={() => {
                                        setModalBody(
                                            labels?.map((label) => (
                                                <Badge mr="1" mb="1" bg="green.100">
                                                    {label}
                                                </Badge>
                                            )),
                                        );
                                        onOpen();
                                    }}
                                >
                                    {translate('global.show_more')}
                                </Button>
                            )}
                        </Flex>
                    );
                }

                return (
                    <Badge mr="1" mb="1" bg="green.100">
                        {label}
                    </Badge>
                );

            default:
                return null;
        }
    };

    const renderDeleteChanges = (params: unknown, category: TCategory) => {
        const { list } = (params as any) || {};

        switch (category) {
            case 'KEYWORD':
                return (
                    <Flex flexWrap="wrap">
                        {(params as TKeywordDeleteChanges).keywords.map((k) => (
                            <Badge mr="1" mb="1">
                                {k.name}
                            </Badge>
                        ))}
                    </Flex>
                );
            case 'PRODUCT_INFO':
            case 'PRODUCT_TITLE':
            case 'PDP':
                return (
                    <Flex flexWrap="wrap">
                        {(params as TProductDeleteChanges).products.map((p) => (
                            <Badge mr="1" mb="1">
                                {p}
                            </Badge>
                        ))}
                    </Flex>
                );
            case 'COMPANY':
                return (
                    <Flex flexWrap="wrap">
                        <Badge mr="1" mb="1">
                            {(params as THistoryChanges).nameId}
                        </Badge>
                    </Flex>
                );

            case 'KEYWORD_CATEGORIES': {
                return renderKeywordCategoriesChanges(params as TKeywordCategoriesChanges);
            }

            case 'COLORS':
            case 'INTERNATIONAL_COMPANY_GROUPS': {
                const { name } = params as TDeleteChanges;
                return (
                    <Flex flexWrap="wrap">
                        <Badge mr="1" mb="1">
                            {name}
                        </Badge>
                    </Flex>
                );
            }

            case 'PICTURE':
                return (
                    <Flex flexWrap="wrap">
                        {list?.slice(0, 3).map((url: string) => (
                            <Badge mr="1" mb="1" textTransform="none">
                                {url}
                            </Badge>
                        ))}
                        {list?.length > 3 && (
                            <Button
                                size="sm"
                                width={'fit-content'}
                                onClick={() => {
                                    setAndShowModalList(list);
                                }}
                            >
                                {translate('more')}
                            </Button>
                        )}
                    </Flex>
                );
            case 'LIST':
                return (
                    <>
                        <Flex flexWrap="wrap">
                            <Text as="span" fontWeight="bold" mr="2">
                                List:
                            </Text>
                            {(params as TListChanges).nameId}
                        </Flex>
                    </>
                );

            case 'CLUSTER': {
                const { label } = params as TClusterChanges;

                return (
                    <Badge mr="1" mb="1">
                        {label}
                    </Badge>
                );
            }

            case 'SHOP_CLUSTER':
            case 'PRODUCT_CLUSTER':
                const { shopId, ean, removed = [] } = params as TClusterHistoryChanges;
                const isShopCluster = category === 'SHOP_CLUSTER';

                return (
                    <Flex flexDir="column">
                        <Text mr="2" fontWeight="bold">
                            {isShopCluster ? translate('shopCluster.column.shop_id') : 'EAN'}:{' '}
                            {isShopCluster ? shopId : ean}
                        </Text>
                        <Flex flexWrap="wrap">
                            <Text mr="2" fontWeight="bold">
                                {translate('cluster.removed')}:
                            </Text>
                            {removed.map((label) => (
                                <Badge mr="1" mb="1">
                                    {label}
                                </Badge>
                            ))}
                        </Flex>
                    </Flex>
                );
            default:
                return null;
        }
    };

    const columns: ColumnDef<THistoryEntry, any>[] = useMemo(() => {
        const tableColumns: (ColumnDef<THistoryEntry, any> | null)[] = [
            {
                header: translate('history.date') as string,
                accessorKey: 'date',
                cell: (info) => {
                    const { original } = info.row;
                    return (
                        <Box>
                            <Text>{dates(original.date).format('L HH:mm')}</Text>
                        </Box>
                    );
                },
            },
            {
                header: translate('history.user') as string,
                accessorKey: 'user',
            },
            {
                header: translate('history.category') as string,
                accessorKey: 'category',
            },
            {
                header: translate('history.action') as string,
                accessorKey: 'action',
                cell: (info) => {
                    const { original } = info.row;
                    return (
                        <Text>
                            {translate(
                                'oldFac' in original.params
                                    ? 'history.PRODUCT_TITLE.UPDATE_FAC'
                                    : `history.${original.category}.${original.action}`,
                            )}
                        </Text>
                    );
                },
            },
            currentCategory &&
            !/KEYWORD|PICTURE|SECTORISATION|LIST|COLORS|INTERNATIONAL_COMPANY_GROUPS|SALES_DATA|CLUSTER/.test(
                currentCategory,
            )
                ? ({
                      header: translate('history.retailer') as string,
                      accessorKey: 'retailer',
                      cell: (info) => {
                          const { original } = info.row as { original: IHistory<'PRODUCT_INFO' | 'PRODUCT_TITLE'> };
                          return <Text>{original.retailer}</Text>;
                      },
                  } as ColumnDef<THistoryEntry, any>)
                : null,
            currentCategory &&
            !/KEYWORD|PICTURE|SECTORISATION|LIST|COLORS|INTERNATIONAL_COMPANY_GROUPS|SALES_DATA|CLUSTER/.test(
                currentCategory,
            )
                ? ({
                      header: translate('history.lang') as string,
                      accessorKey: 'lang',
                      cell: (info) => {
                          const { original } = info.row as { original: IHistory<'PRODUCT_INFO' | 'PRODUCT_TITLE'> };
                          return <Text>{original.lang}</Text>;
                      },
                  } as ColumnDef<THistoryEntry, any>)
                : null,
            {
                header: translate('history.params') as string,
                accessorKey: 'params',
                cell: (info) => {
                    const { original } = info.row;
                    const { params, action, category } = original;
                    switch (action) {
                        case 'ADD':
                            return renderAddChanges(params, category);
                        case 'DELETE':
                            return renderDeleteChanges(params, category);
                        case 'EXPORT':
                            return (
                                <Text>
                                    <Text as="span" fontWeight="bold" mr="2">
                                        {translate('history.type')}
                                    </Text>
                                    {(params as TKeywordExportChanges).type}
                                </Text>
                            );
                        case 'UPDATE':
                            return <>{createUpdateSection(params, category)}</>;
                        case 'UPLOAD':
                            return renderUploadChanges(params, category);
                        case 'ERROR':
                            return (
                                <Text
                                    onClick={intentionalyFloatingPromiseReturn(() =>
                                        handleDownloadNonAcceptedKeywords((params as TKeywordErrorChanges).keywords),
                                    )}
                                    {...linkStyle}
                                    ms="0"
                                >
                                    {translate('history.error.download')}
                                </Text>
                            );
                        default:
                            return <Text>{translate('history.default')}</Text>;
                    }
                },
                meta: { width: '40%', Filter: DefaultColumnFilter },
                filterFn: filterByKeyword,
            },
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<THistoryEntry, any> => e !== null);
        return filteredColumns;
    }, [isLoading, isError, currentCategory, historyEntries]);

    const defaultColumn = useMemo(
        () => ({
            meta: { width: '150px', Filter: DefaultColumnFilter },
        }),
        [],
    );

    const sortees = useMemo(
        () => [
            {
                id: 'date',
                desc: true,
            },
        ],
        [],
    );

    const table = useReactTable<THistoryEntry>({
        data: data as THistoryEntry[],
        columns,
        defaultColumn,
        sortingFns: SORTING_FNS,
        globalFilterFn: filterByKeyword,
        getColumnCanGlobalFilter: () => true,
        initialState: { sorting: sortees },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const renderTableToolbar = () => {
        const DownloadIcon = icons.download;
        const FilterIcon = icons.filter;
        const categories: TCategory[] = [];
        historyEntries?.histories.forEach((h) => {
            if (!categories.includes(h.category)) categories.push(h.category);
        });

        const handleChangeCategory = (e: ChangeEvent<HTMLSelectElement>) => {
            setCurrentCategory(e.target.value as keyof TParamsType);
        };

        return (
            <Flex {...tableToolbarStyle} justifyContent="space-between">
                <Flex>
                    <GlobalFilter
                        globalFilter={table.getState().globalFilter}
                        setGlobalFilter={table.setGlobalFilter}
                    />
                    <IconButton
                        {...iconTableStyle}
                        aria-label="filter history"
                        size="sm"
                        icon={<Icon as={FilterIcon} />}
                    />
                    <IconButton
                        size="sm"
                        {...iconTableStyle}
                        aria-label="bulk delete"
                        icon={<Icon as={DownloadIcon} />}
                    />
                </Flex>
                <Flex>
                    <Select
                        onChange={handleChangeCategory}
                        size="sm"
                        borderColor="gray.200"
                        bg="white"
                        borderRadius={6}
                        value={currentCategory}
                        isDisabled={categories.length === 0}
                    >
                        {categories.length > 0 ? (
                            categories.map((c) => <option value={c}>{translate(`history.${c}`)}</option>)
                        ) : (
                            <option> {translate('history.NO_CATEGORY')}</option>
                        )}
                    </Select>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader icon={{ name: 'history' }} pageTitle={translate(NAME) as string}></PageHeader>
            <Box mt="16px" px="32px">
                {renderTableToolbar()}
                <DITable table={table} isPagination isHeaderFixed />
                <TablePagination table={table} />
            </Box>
            {renderChangesDetailModal()}
        </Box>
    );
};

export { ID, PATH, NAME };
export default HistoryViewer;
